import { useState, useEffect } from 'react';
// components
import Dialog from '../../components/dialog';
import { PasswordField, ButtonField, Label, CodeFields, Message as FormMessage } from '../../components/hook-form';
// @form
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @moment
import moment from 'moment-timezone';
// utils
import { REGEXP_EMAIL, REGEXP_PASSWORD } from '../../utils/regexp';
// styles
import { Tabs, Message, ButtonWrapper, PrimaryButton, TertiaryButton } from '../../styles';
// apis
import { verifyEmail, updateEmail, deleteEmail, changePassword } from '../../apis';

// ----------------------------------------------------------------------

interface Props {
  expirationAt: string;
  email: string;
  setVisible: React.Dispatch<React.SetStateAction<boolean>>;
};

// ----------------------------------------------------------------------

export default function SecurityDialog({ expirationAt, email, setVisible }: Props) {
  const handleClose = () => setVisible(false);

  const emailSchema = yup.object().shape({
    email: yup.string()
      .required()
      .trim()
      .matches(REGEXP_EMAIL, {message: '이메일 형식을 확인해 주세요.'})
  });

  const {
    control,
    setValue,
    handleSubmit
  } = useForm({
    resolver: yupResolver(emailSchema),
    defaultValues: {
      email: email || ''
    }
  });

  const [ confirm, setConfirm ] = useState(null);
  const [ confirmMessage, setConfirmMessage ] = useState(null);
  const handleConfirm = () => setConfirm(null);

  const [ success, setSuccess ] = useState(null);
  const handleSuccess = () => setSuccess(null);

  const [ start, setStart ] = useState(null);
  const [ remain, setRemain ] = useState(0);

  const [ requestId, setRequestId ] = useState(null);
  const [ code, setCode ] = useState('');

  const minutes = String( Math.floor(remain / 60) ).padStart(2, '0');
  const seconds = String(remain % 60).padStart(2, '0');

  useEffect(() => {
    const timer = setInterval(() => {
      setRemain((current) => current - 1);
    }, 1000);

    if ( remain <= 0 ) {
      clearInterval(timer);
      setStart(null);
      if ( start ) setMessage('유효 시간이 만료되었습니다. 인증 메일을 재전송해 주세요.');
    }

    return () => clearInterval(timer);
  }, [remain]);

  const [ message, setMessage ] = useState(null);

  const handleDelete = () => {
    deleteEmail()
      .then(() => {
        setValue('email', '');
        handleConfirm();
      })
      .catch(() => setConfirmMessage('이메일을 등록 해제할 수 없습니다. 나중에 다시 시도해 주세요.'));
  };

  const onSubmit = async (data: { email?: string }) => {
    if ( !email ) {
      await verifyEmail(data.email)
        .then((response) => {
          const { requestId, expirationAt } = response;
          setRequestId(requestId);
          setStart(true);
          setRemain(moment(expirationAt).diff(moment().utc(), 'seconds'));
          setMessage(null);
          setCode('');
        })
        .catch((error) => setMessage(error.message));
    } else {
      setConfirm('이메일을 등록 해제 하시겠습니까?');
    }
  };

  const onEmail = async () => {
    if ( String(code).length === 6 ) {
      await updateEmail(requestId, code)
        .then(() => {
          setSuccess(true);
        })
        .catch((error) => {
          const code = error.response.data.code;
          if ( code === 'ERR-028' ) return setRemain(0);
          if ( code === 'ERR-029' ) return setMessage('인증 코드를 다시 확인해주세요.');
          setMessage('이메일을 등록할 수 없습니다. 나중에 다시 시도해 주세요.')
        });
    } else {
      setMessage('인증 코드를 다시 확인해주세요.');
    }
  };

  return (
    <>
      <Dialog
        title='계정 관리'
        handleClose={handleClose}
      >
        <p style={{'textAlign': 'left', 'marginBottom': '30px'}}>계정 만료일 : { expirationAt }</p>
        <form onSubmit={ handleSubmit(onSubmit) } noValidate>
          <ButtonField
            name='email'
            label='이메일'
            disabled={email || remain > 0 ? true : false}
            control={control}
            action={
              <TertiaryButton disabled={remain > 0 ? true : false}>{email ? '등록 해제' : '인증 요청'}</TertiaryButton>
            }
          />

          { !email &&
            <>
              <div>
                <Label>인증 코드</Label>
                <CodeFields $disabled={remain > 0 ? false : true}>
                  <input type='number' disabled={remain > 0 ? false : true} value={code} onChange={(event) => setCode(event.target.value)} min={0} max={999999} step={1}  />
                  { remain > 0 &&
                    <p>{ minutes }:{ seconds }</p>
                  }
                </CodeFields>
              </div>
        
              <Message $visible={message ? true : false}>{ message }</Message>
        
              <ButtonWrapper>
                <PrimaryButton type='button' disabled={remain > 0 ? false : true} onClick={() => onEmail()}>
                  등록
                </PrimaryButton>
                <TertiaryButton type='button' onClick={() => handleClose()} >
                  취소
                </TertiaryButton>
              </ButtonWrapper>
            </>
          }
        </form>
      </Dialog>
      
      { confirm &&
        <Dialog
          title='이메일 등록 해제'
          handleClose={handleConfirm}
        >
          <p>{ confirm }</p>

          <Message $visible={confirmMessage ? true : false}>{ confirmMessage }</Message>

          <ButtonWrapper>
            <PrimaryButton onClick={() => handleDelete()}>등록 해제</PrimaryButton>
            <TertiaryButton type='button' onClick={() => setConfirm(false)}>취소</TertiaryButton>
          </ButtonWrapper>
        </Dialog>
      }
      
      { success &&
        <Dialog
          handleClose={handleSuccess}
        >
          <p style={{'marginBottom': '30px'}}>이메일 등록이 완료되었습니다.</p>

          <PrimaryButton type='button' onClick={() => setSuccess(false)}>확인</PrimaryButton>
        </Dialog>
      }
    </>  
  );
}